<template>
  <div class="contenedor-principal">
    <h1>Flysch de Bizkaia: Descubre la Geología Oculta de la Costa Vasca</h1>

    <p>
      La <strong>Costa Vasca</strong> esconde tesoros geológicos de valor incalculable. Entre ellos, el
      <strong>Flysch</strong> se alza como uno de los fenómenos más impresionantes, especialmente en el tramo que
      recorre Bizkaia y Gipuzkoa. En Geobizi, integramos el <strong>geoturismo</strong> con la educación
      ambiental para desvelar la historia de nuestro planeta escrita en las rocas. ¿Sabías que estas formaciones
      revelan secretos de la extinción de los dinosaurios?
    </p>

    <h2>¿Qué es el Flysch y por qué es tan relevante?</h2>
    <p>
      El término <strong>"Flysch"</strong> se refiere a una secuencia de rocas sedimentarias que alternan
      capas duras (como areniscas o calizas) y capas blandas (como margas o arcillas). Esta alternancia, visible
      en los acantilados y plataformas de abrasión, es el resultado de la deposición rítmica de sedimentos en un
      ambiente marino profundo hace millones de años.
    </p>

    <p>
      Su relevancia no es solo visual. Las capas del <strong>flysch bizkaino</strong> son como las páginas
      de un libro que narra la historia geológica de la Tierra, permitiendo a los geólogos datar eventos
      cruciales, como el <strong>límite K-Pg</strong>
      (Cretácico-Paleógeno), asociado a la extinción masiva que acabó con los dinosaurios. Este es un punto de
      interés fundamental para el <strong>geoturismo en Bizkaia</strong>.
    </p>

    <h2>Flysch en Bizkaia: El Tesoro de la Costa</h2>
    <p>
      Aunque el flysch más famoso es el de Zumaia (Gipuzkoa), Bizkaia cuenta con tramos igualmente
      fascinantes, aunque menos conocidos. La clave para posicionarte es la especificidad. Los acantilados y la
      rasa mareal de la costa vizcaína presentan estas formaciones en lugares como Getxo, Barrika o Sopela.
    </p>
    <p>
      En estas zonas del <strong>Flysch de Bizkaia</strong>, las capas se encuentran a menudo plegadas y
      verticalizadas debido a la orogenia Alpina, creando un paisaje espectacular. Nuestras <strong>rutas de
        geoturismo</strong> te llevan a interpretarlos, conectando la geología con la ecología marina y la biodiversidad
      que se aferra a estas estructuras rocosas.
    </p>

    <h2>Geoturismo y Educación Ambiental con Geobizi</h2>
    <p>
      En <strong>Geobizi</strong>, creemos que el <strong>geoturismo</strong> no es solo mirar rocas, sino
      entender los procesos que las formaron y cómo influyen en el ecosistema actual. Por eso, nuestras
      excursiones por el <strong>flysch de Bizkaia</strong> se centran en:
    </p>

    <ul>
      <li><strong>Interpretación Geológica:</strong> Aprender a leer las capas, fallas y pliegues.</li>
      <li><strong>Biodiversidad Litoral:</strong> Observación de las especies adaptadas a la rasa mareal.</li>
      <li><strong>Historia del Planeta:</strong> Conectar las formaciones locales con eventos globales,
        como la caída del meteorito que impactó en Yucatán.</li>
    </ul>

    <h2>¿Cómo interpretar las rocas del Flysch?</h2>
    <p>
      Los elementos clave a buscar en el Flysch son las capas de diferente grosor. La capa más dura y gruesa
      suele ser <strong>caliza o arenisca</strong>
      (depositada en el momento de la corriente de turbidez), y la capa más delgada y blanda es <strong>marga o
        arcilla</strong> (depositada durante el periodo de calma).
    </p>

    <p>
      Al participar en una ruta de <strong>Geobizi</strong>, te proporcionaremos las herramientas para
      descifrar este lenguaje pétreo y convertir una simple caminata costera en un viaje al pasado geológico de
      Bizkaia.
    </p>

    <div class="caja-recurso">
      <h3>Profundiza en la didáctica del Flysch</h3>
      <p>
        Hemos preparado un documento detallado con recursos didácticos sobre el Flysch de Bizkaia. Ideal para
        docentes, familias o personas con ganas de aprender más antes de su visita.
      </p>
      <div class="botones-recurso">
        <!-- El enlace de descarga se mantiene como <a> porque apunta a un archivo externo -->
        <a href="/descargas/documentos/Flyschbizkaia-didactica.pdf" download="Flyschbizkaia-didactica.pdf"
          class="btn-descargar-directo">
          Descargar PDF
        </a>

        <!-- El enlace de navegación ahora apunta a la nueva ruta jerárquica -->
        <router-link to="/servicios/descargas" class="btn-ver-mas">
          Ver más recursos
        </router-link>
      </div>
    </div>

    <h2>¡Únete a una Ruta Geológica!</h2>
    <p>
      Si te apasiona la geología y quieres experimentar el <strong>Flysch de Bizkaia</strong> de una manera
      educativa y consciente, te invitamos a consultar nuestras <a href="/calendario"
        title="Rutas Geológicas Geobizi">rutas de geoturismo</a> en el calendario. ¡El próximo capítulo de la historia
      de la Tierra te espera en la costa!
    </p>

    <p class="social-media"><b>¡Síguenos en redes sociales para ver imágenes espectaculares de nuestras rutas por el
        litoral!</b></p>
    <div class="redes-sociales">
      <a href="https://www.instagram.com/geotxiki/" target="_blank" rel="noopener noreferrer"
        title="Geobizi en Instagram">
        <img src="/imagenes/iconos/instagram.png" alt="Geobizi en Instagram" class="social-icon" loading="lazy" />
      </a>
      <a href="https://www.facebook.com/geobizirik/" target="_blank" rel="noopener noreferrer"
        title="Geobizi en Facebook">
        <img src="/imagenes/iconos/facebook.png" alt="Geobizi en Facebook" class="social-icon" loading="lazy" />
      </a>
      <a href="https://www.tiktok.com/@geobizi" target="_blank" rel="noopener noreferrer" title="Geobizi en TikTok">
        <img src="/imagenes/iconos/tiktok.png" alt="Geobizi en TikTok" class="social-icon" loading="lazy" />
      </a>
    </div>

    <div class="imagen-trio">
      <div>
        <img class="img" src="/imagenes/blog/detalle/flyschbizkaia/sopelaefecto.avif"
          alt="Acantilado de Flysch en Bizkaia mostrando pronunciados pliegues rocosos"
          title="FlyschBizkaia — Geología costera" loading="lazy" />
      </div>
      <div>
        <img class="img" src="/imagenes/blog/detalle/flyschbizkaia/coladragonflysch.avif"
          alt="Rasa mareal con formaciones del Flysch de Bizkaia en bajamar" title="Flysch — Rasa mareal"
          loading="lazy" />
      </div>
      <div>
        <img class="img" src="/imagenes/blog/detalle/flyschbizkaia/geoturismo.avif"
          alt="Grupo de geoturismo observando las capas del Flysch de Bizkaia con Geobizi"
          title="Geoturismo en Flysch de Bizkaia" loading="lazy" />
      </div>
    </div>

  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

// URL optimizada
const pageUrl = 'https://www.geobizi.com/blog/flysch-bizkaia'
const ogImage = 'https://www.geobizi.com/imagenes/blog/detalle/flyschbizkaia/sopelaefecto.avif' // Asegúrate de que esta ruta existe

useHead({
  title: 'Flysch de Bizkaia: Rutas Geológicas y Geoturismo | Geobizi',
  meta: [
    { name: 'description', content: 'Descubre el Flysch de Bizkaia con nuestras rutas de geoturismo. Aprende geología, identifica fósiles y explora la costa vasca en familia.' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Geobizi' },
    { name: 'language', content: 'es' },
    { property: 'og:title', content: 'Flysch de Bizkaia: Rutas Geológicas y Geoturismo | Geobizi' },
    { property: 'og:description', content: 'Descubre el Flysch de Bizkaia con nuestras rutas de geoturismo. Aprende geología y explora la costa vasca.' },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' }
  ],
  link: [
    { rel: 'canonical', href: pageUrl },
    { rel: 'image_src', href: ogImage }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Flysch de Bizkaia: Descubre la Geología Oculta de la Costa Vasca",
        "description": "Descubre el Flysch de Bizkaia con Geobizi. Rutas de geoturismo y divulgación geológica en la costa vasca.",
        "image": ogImage,
        "author": {
          "@type": "Organization",
          "name": "Geobizi",
          "url": "https://www.geobizi.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Geobizi",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.geobizi.com/imagenes/GeobiziLogo.png"
          },
          "sameAs": [
            "https://www.facebook.com/geobizirik/",
            "https://www.instagram.com/geotxiki/",
            "https://www.tiktok.com/@geobizi",
            "https://www.youtube.com/channel/UCw-C_J0y-jKHp7Zx92lsKfg"
          ]
        },
        "datePublished": "2025-02-15T08:00:00+01:00",
        "dateModified": "2026-04-14T10:00:00+02:00",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": pageUrl
        }
      })
    }
  ]
})
</script>

<style scoped>
/* CAJA DE RECURSOS */
.caja-recurso {
  background-color: var(--megashoftgreen);
  border-left: 4px solid var(--shoftgreen);
  padding: 1.5rem;
  margin: 2.5rem 0;
  border-radius: 0 0.5rem 0.5rem 0;
}

.caja-recurso h3 {
  margin-top: 0;
  color: var(--lightgreen);
  font-size: 1.2rem;
}

.caja-recurso p {
  margin-bottom: 1.5rem;
  color: var(--darkgrey);
}

.botones-recurso {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-descargar-directo {
  background-color: var(--supershoftgreen);
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.btn-descargar-directo:hover {
  background-color: var(--shoftgreen);
  color: var(--white);
}

.btn-ver-mas {
  background-color: transparent;
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border: 1px solid var(--shoftgreen);
  border-radius: 4px;
  transition: all 0.3s;
}

.btn-ver-mas:hover {
  background-color: var(--shoftgreen);
  color: white;
}

/* REDES SOCIALES */
.social-media {
  margin-top: 2rem;
  color: var(--darkgrey);
}

.redes-sociales {
  margin-left: 1rem;
  display: flex;
  align-items: center;
}

.social-icon {
  width: 2.5rem;
  margin-right: 1rem;
  margin-bottom: 1.5rem;
  transition: transform 0.3s;
}

.social-icon:hover {
  transform: scale(1.1);
}

/* LISTAS CON HOJITAS */
.contenedor-principal ul {
  list-style-type: none;
  padding-left: 0;
}

.contenedor-principal li {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 0.8rem;
  line-height: 1.5;
}

.contenedor-principal li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  width: 1.2rem;
  height: 1.2rem;
  background-image: url('../../assets/Hojitas.png');
  background-size: contain;
  background-repeat: no-repeat;
}
</style>